import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography, AppButton, InnoveraLoading } from "@libs/zapi-ui/custom";
import { SlideCard } from "./SlideCard";
import { SlidesSidebar } from "./SlidesSidebar";
import { PdfPreview } from "./PdfPreview";
import { usePresentation } from "@/zapi-frontend/shared/hooks/usePresentation";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { InnoveraIcon } from "@libs/zapi-ui/assets";
import {
  ArrowClockwiseIcon,
  ArrowSquareOut,
  BookOpenIcon,
  ClipboardTextIcon,
  MonitorPlayIcon
} from "@phosphor-icons/react";
import { ChevronLeft } from "lucide-react";
import { OutlineEmpty } from "./OutlineEmpty";
import { RefreshOutlineButton } from "./RefreshOutlineButton";

import { PresentationInstructionsModal } from "./PresentationInstructionsModal";
import { formatGeneratedTime } from "../lib/formatGeneratedTime";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { PresentationChat } from "./PresentationChat";
import { usePresentationChatStore } from "../hooks/usePresentationChat";
import { DockedChatIsland } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { useChatUnreadIndicator } from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useParams, useSearchParams } from "react-router-dom";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { projectPageBackgroundStyle } from "@/zapi-frontend/shared/lib/projectTheme";
import { TViewMode } from "../model/types";

export function PresentationPage() {
  const { t } = useTranslation("projectHome");
  const {
    slides,
    isLoading,
    isSlidesGenerating,
    isSlidesRegenerating,
    isGammaGenerating,
    gammaPdfUrl,
    gammaWebUrl,
    outlineWasRefreshed,
    hasSlides,
    presentation,
    presentationExists,
    outlineGeneratedAt,
    gammaGeneratedAt,
    allowRegeneration,
    updateSlide,
    deleteSlide,
    addSlide,
    reorderSlides,
    generateSlides,
    regenerateSlides,
    generateGammaPreview,
    isSaving
  } = usePresentation();
  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view") as TViewMode;
  const { id: projectId = "" } = useParams();
  const assistantMessageCount = usePresentationChatStore(
    (s) => (s.chatHistory[projectId] ?? []).filter((m) => m.role !== "user" && !m.id.startsWith("notification-")).length
  );
  const isChatHistoryEmpty = usePresentationChatStore((s) => (s.chatHistory[projectId] ?? []).length === 0);
  const {
    hasUnread: hasUnreadChat,
    markSeen: markChatSeen,
    markUnread: markChatUnread
  } = useChatUnreadIndicator(assistantMessageCount, {
    firstVisitKey: projectId ? `slides:${projectId}` : undefined,
    conversationKey: projectId
  });

  // `PresentationChatMessageList` renders the "Your outline is ready!" bubble itself
  // rather than storing it, so it never reaches the message count the dot is derived from.
  const showsOutlineReadyMessage = isChatHistoryEmpty && hasSlides && !isSlidesGenerating;
  // Whether that bubble is up is unknowable until the project reports whether a
  // presentation exists and, if one does, it has been fetched. Read any earlier, an
  // existing outline looks absent and then arrives, which is indistinguishable from
  // one the user just generated. A presentation still generating needs no fetch — the
  // bubble is replaced by the generation steps until it finishes.
  const isOutlineResolved =
    selectedProject !== null &&
    !isFetchProjectLoading &&
    (!presentationExists || presentation !== null || isSlidesGenerating);

  const slidesContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const [outlineSlideIndex, setOutlineSlideIndex] = useState(0);
  const [previewPageIndex, setPreviewPageIndex] = useState(1);
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(false);

  function selectSlideHandler(index: number) {
    isProgrammaticScrollRef.current = true;
    setOutlineSlideIndex(index);
    const slideElement = document.getElementById(`slide-${slides[index].id}`);
    if (slideElement) {
      slideElement.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 500);
    }
  }

  function handleSlidesScroll() {
    if (isProgrammaticScrollRef.current) return;
    if (slides.length === 0) return;

    const container = slidesContainerRef.current;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.top + containerRect.height / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      const el = document.getElementById(`slide-${slide.id}`);
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const elCenter = rect.top + rect.height / 2;
      const distance = Math.abs(containerCenter - elCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setOutlineSlideIndex(closestIndex);
  }

  function handleAddSlide() {
    const newSlideId = addSlide(outlineSlideIndex);
    const newIndex = outlineSlideIndex + 1;

    setTimeout(() => {
      setOutlineSlideIndex(newIndex);
      const slideElement = document.getElementById(`slide-${newSlideId}`);
      if (slideElement) {
        slideElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 50);
  }

  const isPageLoading = isLoading || isFetchProjectLoading;
  const isDisabledGeneratingSlides = isSlidesGenerating || !hasSlides || isSaving;
  const showRefreshSlides = (outlineWasRefreshed || allowRegeneration) && gammaGeneratedAt != null;

  function switchToOutline() {
    setSearchParams({ tab: "presentation", view: "outline" }, { replace: true });
    setTimeout(() => {
      const slide = slides[outlineSlideIndex];
      if (!slide) return;

      const slideElement = document.getElementById(`slide-${slide.id}`);
      if (!slideElement) return;

      const container = slidesContainerRef.current;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = slideElement.getBoundingClientRect();
      const isInViewport = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom;

      if (!isInViewport) {
        isProgrammaticScrollRef.current = true;
        slideElement.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 500);
      }
    }, 50);
  }

  const switchToPreview = () => setSearchParams({ tab: "presentation", view: "preview" }, { replace: true });
  const handleCreateSlides = () => generateGammaPreview();
  const handleGenerateSlides = () => generateSlides();

  useEffect(() => {
    if (!viewMode) setSearchParams({ tab: "presentation", view: "outline" }, { replace: true });
  }, [viewMode, setSearchParams]);

  const seenOutlineReadyRef = useRef<{ projectId: string; shown: boolean } | null>(null);
  useEffect(() => {
    if (!isOutlineResolved) return;
    const seen = seenOutlineReadyRef.current;
    seenOutlineReadyRef.current = { projectId, shown: showsOutlineReadyMessage };
    if (!seen || seen.projectId !== projectId) return;
    if (!seen.shown && showsOutlineReadyMessage) markChatUnread();
  }, [isOutlineResolved, showsOutlineReadyMessage, projectId, markChatUnread]);

  return (
    <div
      className={cn("w-full h-[calc(100vh-56px)] flex p-2 bg-projectPage", viewMode === "preview" ? "" : "gap-2")}
      style={projectPageBackgroundStyle(selectedProject?.iconColor)}
    >
      <div className="w-full h-full flex flex-col bg-projectPage">
        <div className="min-h-14 px-4 w-full bg-white flex items-center">
          {viewMode === "preview" ? (
            <div className="w-full grid grid-cols-[1fr_auto_1fr] items-center">
              <div className="justify-self-start">
                <AppButton
                  appearance="text"
                  onClick={switchToOutline}
                  data-testid="presentation-back-to-outline"
                  startAdornment={<ChevronLeft className="w-4 h-4" />}
                >
                  {t("presentation.backToOutline")}
                </AppButton>
              </div>
              <FigmaTypography variant="label-1" color="zinc900" className="min-w-0 truncate leading-normal">
                {selectedProject?.name}
              </FigmaTypography>
              {isLabs && (
                <div className="justify-self-end">
                  <AppButton
                    appearance="outlined"
                    color="zinc150"
                    onClick={() => gammaWebUrl && window.open(gammaWebUrl, "_blank")}
                    disabled={!gammaWebUrl}
                    data-testid="presentation-open-in-gamma"
                    startAdornment={<ArrowSquareOut size={16} />}
                  >
                    {t("presentation.openInGamma")}
                  </AppButton>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full flex flex-wrap justify-between items-center gap-y-2 py-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center border border-innoveraZinc-100 rounded-full">
                  <ClipboardTextIcon size={18} className="text-innoveraZinc-500" />
                </div>
                <FigmaTypography variant="heading-3" color="zinc900">
                  {t("presentation.outlineTitle")}
                </FigmaTypography>
              </div>
              <div className="flex items-center gap-2">
                {outlineGeneratedAt && (
                  <FigmaTypography variant="body-3" color="zinc500">
                    {formatGeneratedTime(outlineGeneratedAt, outlineWasRefreshed ? "refreshed" : "created", t)}
                  </FigmaTypography>
                )}
                {isLabs && (
                  <AppButton
                    appearance="outlined"
                    color="zinc150"
                    size="icon"
                    title={t("instructionsModal.outlineInstructions")}
                    onClick={() => setIsInstructionsOpen(true)}
                    disabled={!hasSlides}
                  >
                    <BookOpenIcon size={18} />
                  </AppButton>
                )}
                {hasEdit && (
                  <RefreshOutlineButton
                    outlineGeneratedAt={outlineGeneratedAt}
                    hasSlides={hasSlides}
                    onRefresh={regenerateSlides}
                    isRefreshing={isSlidesRegenerating}
                    disabled={isGammaGenerating || isSaving || isSlidesGenerating}
                  />
                )}

                {hasEdit && gammaPdfUrl && !allowRegeneration && !isGammaGenerating ? (
                  <AppButton
                    disabled={isSlidesRegenerating}
                    onClick={switchToPreview}
                    color="zinc100"
                    className="min-w-[142px] bg-innoveraZinc-900 text-white hover:bg-innoveraZinc-800"
                    data-testid="presentation-preview-slides"
                    startAdornment={<MonitorPlayIcon width={16} height={16} className="[&>path]:fill-white" />}
                  >
                    {t("presentation.previewSlides")}
                  </AppButton>
                ) : (
                  hasEdit && (
                    <>
                      <div className="w-[1px] h-4 bg-innoveraZinc-200" />
                      <AppButton
                        onClick={handleCreateSlides}
                        disabled={isDisabledGeneratingSlides}
                        isLoading={isGammaGenerating}
                        loadingText={showRefreshSlides ? t("presentation.refreshing") : t("presentation.creating")}
                        data-testid="presentation-create-slides"
                        className="min-w-[142px]"
                        startAdornment={
                          showRefreshSlides ? (
                            <ArrowClockwiseIcon size={16} />
                          ) : (
                            <InnoveraIcon
                              width={16}
                              height={16}
                              className={cn(
                                isDisabledGeneratingSlides ? "[&>path]:fill-innoveraZinc-400" : "[&>path]:fill-white"
                              )}
                            />
                          )
                        }
                      >
                        {showRefreshSlides ? t("presentation.refreshSlides") : t("presentation.createSlides")}
                      </AppButton>
                    </>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        <div className="h-[calc(100%-56px)] flex pt-2">
          {viewMode === "outline" && (
            <div className="flex w-full min-w-0 gap-2">
              <SlidesSidebar
                slides={slides}
                selectedIndex={outlineSlideIndex}
                isGenerating={isSlidesGenerating || isPageLoading}
                onSelectSlide={selectSlideHandler}
                onDeleteSlide={deleteSlide}
                onAddSlide={handleAddSlide}
                onReorderSlides={reorderSlides}
              />
              {isPageLoading && (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <InnoveraLoading size={90} />
                  <FigmaTypography variant="body-1">{t("presentation.loadingOutline")}</FigmaTypography>
                </div>
              )}

              {!isPageLoading && (
                <>
                  {isSlidesGenerating && (
                    <div className="w-full h-full bg-white flex flex-col items-center justify-center gap-3">
                      <InnoveraLoading size={40} />
                      <FigmaTypography variant="heading-2" color="zinc900">
                        {t("presentation.creatingOutline")}
                      </FigmaTypography>
                      <FigmaTypography variant="body-2" color="zinc500" className="text-center">
                        {t("presentation.analyzingLine1")}
                        <br />
                        {t("presentation.analyzingLine2")}
                      </FigmaTypography>
                    </div>
                  )}
                  {hasSlides && !isSlidesGenerating && (
                    <div
                      ref={slidesContainerRef}
                      onScroll={handleSlidesScroll}
                      className="w-full min-w-0 min-h-0 overflow-y-auto"
                    >
                      <div className="w-full grid gap-2 pb-4">
                        {slides.map((slide, index) => (
                          <SlideCard
                            key={slide.id}
                            slide={slide}
                            index={index}
                            selectedIndex={outlineSlideIndex}
                            onUpdate={updateSlide}
                            onDelete={deleteSlide}
                            onSelect={selectSlideHandler}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {!hasSlides && !isSlidesGenerating && (
                    <OutlineEmpty
                      onGenerate={handleGenerateSlides}
                      onOpenInstructions={() => setIsInstructionsOpen(true)}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {viewMode === "preview" && (
            <div className="flex w-full min-h-0 overflow-hidden justify-center">
              {!isGammaGenerating && (
                <PdfPreview
                  pdfUrl={gammaPdfUrl}
                  initialPage={previewPageIndex}
                  onPageChange={setPreviewPageIndex}
                  isPageLoading={isPageLoading}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {viewMode !== "preview" && (
        <DockedChatIsland hasUnread={hasUnreadChat} onOpenChange={markChatSeen}>
          <PresentationChat selectedSlideIndex={outlineSlideIndex} />
        </DockedChatIsland>
      )}

      <PresentationInstructionsModal open={isInstructionsOpen} onClose={() => setIsInstructionsOpen(false)} />
    </div>
  );
}
